import { useState } from 'react';
import {
  MessageSquare,
  Mail,
  MailOpen,
  Reply,
  CalendarCheck,
  CalendarPlus,
  StickyNote,
  Phone,
  ChevronDown,
} from 'lucide-react';
import {
  useApprenantCommunications,
  type CommEvent,
  type CommEventType,
} from '../hooks/useApprenantCommunications.js';
import { CollapsibleSlice } from '../../../components/CollapsibleSlice.js';

interface Props {
  apprenantId: string;
}

type Tab = 'all' | 'emails' | 'rdv' | 'notes';

export function CommunicationsCard({ apprenantId }: Props) {
  const { data: c, isLoading, error } = useApprenantCommunications(apprenantId);
  const [activeTab, setActiveTab] = useState<Tab>('all');

  if (isLoading || error || !c) {
    return (
      <CollapsibleSlice
        title="Communications"
        icon={<MessageSquare className="text-primary-500 h-4 w-4" />}
      >
        <div className="text-sm text-neutral-500">{error?.message ?? 'Chargement...'}</div>
      </CollapsibleSlice>
    );
  }

  const counts = {
    all: c.events.length,
    emails: c.events.filter((e) => e.type.startsWith('email')).length,
    rdv: c.events.filter((e) => e.type.startsWith('rdv')).length,
    notes: c.events.filter((e) => e.type === 'note').length,
  };

  const filtered = c.events.filter((e) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'emails') return e.type.startsWith('email');
    if (activeTab === 'rdv') return e.type.startsWith('rdv');
    if (activeTab === 'notes') return e.type === 'note';
    return false;
  });

  return (
    <CollapsibleSlice
      title="Communications"
      icon={<MessageSquare className="text-primary-500 h-4 w-4" />}
      badge={
        <div className="flex items-center gap-1">
          <button className="bg-primary-50 text-primary-700 hover:bg-primary-100 rounded px-2 py-0.5 text-[10px] font-semibold">
            <Mail className="inline h-3 w-3" /> Email
          </button>
          <button className="bg-primary-50 text-primary-700 hover:bg-primary-100 rounded px-2 py-0.5 text-[10px] font-semibold">
            <CalendarPlus className="inline h-3 w-3" /> RDV
          </button>
          <button className="bg-primary-50 text-primary-700 hover:bg-primary-100 rounded px-2 py-0.5 text-[10px] font-semibold">
            <StickyNote className="inline h-3 w-3" /> Note
          </button>
        </div>
      }
      summary={
        <div>
          {/* Prochain RDV — toujours visible */}
          {c.nextScheduled && (
            <div className="border-primary-200 bg-primary-50 rounded-lg border p-2.5">
              <div className="text-primary-700 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
                <CalendarCheck className="h-3 w-3" />
                Prochain RDV
              </div>
              <div className="mt-0.5 text-sm font-medium text-neutral-900">
                {c.nextScheduled.subject}
              </div>
              <div className="text-[10px] text-neutral-600">
                {new Date(c.nextScheduled.date).toLocaleString('fr-FR', {
                  dateStyle: 'full',
                  timeStyle: 'short',
                })}
              </div>
            </div>
          )}
          {c.lastContactDate && (
            <div className="mt-2 text-[10px] text-neutral-500">
              Dernier contact :{' '}
              <span className="font-medium text-neutral-700">
                {new Date(c.lastContactDate).toLocaleDateString('fr-FR')}
              </span>{' '}
              ({daysAgo(c.lastContactDate)} jours) · {counts.emails} emails · {counts.rdv} RDV ·{' '}
              {counts.notes} notes
            </div>
          )}
        </div>
      }
    >
      {/* Tabs */}
      <div className="mb-3 flex gap-1 border-b border-neutral-200">
        <TabBtn
          label="Tous"
          count={counts.all}
          active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
        />
        <TabBtn
          label="Emails"
          count={counts.emails}
          active={activeTab === 'emails'}
          onClick={() => setActiveTab('emails')}
        />
        <TabBtn
          label="RDV"
          count={counts.rdv}
          active={activeTab === 'rdv'}
          onClick={() => setActiveTab('rdv')}
        />
        <TabBtn
          label="Notes"
          count={counts.notes}
          active={activeTab === 'notes'}
          onClick={() => setActiveTab('notes')}
        />
      </div>

      {/* Fil d'événements */}
      <ul className="relative space-y-2 border-l border-neutral-200 pl-4">
        {filtered.map((event) => (
          <CommRow key={event.id} event={event} />
        ))}
      </ul>
    </CollapsibleSlice>
  );
}

// ────────────────────────────────────────────────────────────────────

function TabBtn({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 border-b-2 px-2.5 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'border-primary-600 text-primary-700'
          : 'border-transparent text-neutral-500 hover:text-neutral-700'
      }`}
    >
      {label}
      <span
        className={`rounded px-1 text-[10px] ${active ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-500'}`}
      >
        {count}
      </span>
    </button>
  );
}

function CommRow({ event }: { event: CommEvent }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isFuture = new Date(event.date) > new Date();
  const hasBody = !!event.body;

  return (
    <li className="relative">
      <span
        className={`absolute -left-[1.30rem] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
          isFuture ? 'border-primary-400 bg-white' : 'border-neutral-300 bg-white'
        }`}
      >
        <CommIcon type={event.type} />
      </span>

      {/* Ligne cliquable si body */}
      <button
        type="button"
        onClick={() => hasBody && setIsExpanded((x) => !x)}
        className={`group block w-full text-left ${hasBody ? 'hover:text-primary-700 cursor-pointer' : 'cursor-default'}`}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-medium text-neutral-800">{event.subject}</span>
          {hasBody && (
            <ChevronDown
              className={`h-3 w-3 flex-shrink-0 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          )}
        </div>
        <div className="text-[10px] text-neutral-500">
          {new Date(event.date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
          {event.author && <span> · {event.author}</span>}
          {event.detail && <span className="italic"> · {event.detail}</span>}
          {event.emailMeta?.openedAt && <span className="ml-1 text-emerald-600">· 👁 Ouvert</span>}
          {event.emailMeta?.repliedAt && <span className="text-primary-600 ml-1">· ↩ Répondu</span>}
        </div>
      </button>

      {/* Body expandable */}
      {isExpanded && event.body && (
        <div className="ml-0 mt-1.5 rounded border border-neutral-200 bg-neutral-50 p-2.5">
          <pre className="whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-neutral-700">
            {event.body}
          </pre>
          {event.emailMeta?.replyExcerpt && (
            <div className="mt-2 border-t border-neutral-200 pt-2">
              <div className="text-primary-700 text-[10px] font-semibold uppercase tracking-wider">
                ↩ Réponse reçue
              </div>
              <div className="mt-0.5 text-[11px] italic text-neutral-700">
                « {event.emailMeta.replyExcerpt} »
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function CommIcon({ type }: { type: CommEventType }) {
  const props = { className: 'h-2.5 w-2.5' };
  switch (type) {
    case 'email_sent':
      return <Mail {...props} />;
    case 'email_opened':
      return <MailOpen {...props} />;
    case 'email_replied':
      return <Reply {...props} />;
    case 'rdv_past':
      return <CalendarCheck {...props} />;
    case 'rdv_future':
      return <CalendarPlus {...props} />;
    case 'note':
      return <StickyNote {...props} />;
    case 'call':
      return <Phone {...props} />;
  }
}

function daysAgo(iso: string): number {
  const d = new Date(iso).getTime();
  return Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
}
